import {dsMembershipLabel} from "@/utils/clubSnapshot";
import moment from "moment";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsBar,
	DsGrid,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissingSources,
	DsPreviewNotice,
	DsSection,
	DsStatus,
	DsTable,
	DsSkeleton,
} from "@/components/DsKit";

import {useClubCertificates, useClubSnapshot} from "@/utils/hooks";
import {formatEuro, playerName} from "@/utils/clubSnapshot";

const CERT_LABEL = {
	expired: "Scaduto",
	missing: "Assente",
	expiring: "In scadenza",
	unknown: "Non verificabile",
};

const CERT_STATUS = {
	expired: "alert",
	missing: "alert",
	expiring: "warn",
	unknown: "none",
};

// Esempi per gli ambiti organizzativi non ancora gestiti
const PREVIEW_BLOCKS = [
	{
		title: "Tesseramenti",
		value: "182",
		unit: "tesserati",
		lines: ["6 pratiche in lavorazione", "2 nulla osta da richiedere"],
		status: "warn",
	},
	{
		title: "Iscrizioni nuova stagione",
		value: "64%",
		unit: "conferme ricevute",
		lines: [
			"Chiusura iscrizioni: 30 giugno",
			"18 nuovi iscritti scuola calcio",
		],
		status: "ok",
	},
	{
		title: "Documenti e liberatorie",
		value: "11",
		unit: "documenti mancanti",
		lines: ["Privacy e liberatorie foto", "Moduli trasporto minori"],
		status: "warn",
	},
];

function DsSecretariatPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});
	const {data: certificates, loading: certificatesLoading} =
		useClubCertificates({snapshot: s});

	const teams = s?.teams || [];
	const fees = teams.reduce(
		(acc, t) => {
			["expected", "paid", "overdue", "overduePlayers", "count"].forEach(
				(k) => (acc[k] += t.fees[k]),
			);
			return acc;
		},
		{expected: 0, paid: 0, overdue: 0, overduePlayers: 0, count: 0},
	);

	const certRows = certificates?.tracked
		? [...certificates.byPlayer.entries()]
				.filter(([, c]) => c.status !== "ok")
				.map(([id, c]) => ({
					id,
					...c,
					teams: s.teamsByPlayer.get(id) || [],
					player: s.playerById.get(id),
				}))
				.sort((a, b) => (a.daysLeft ?? -9999) - (b.daysLeft ?? -9999))
		: [];
	const certIssues = certRows.filter((r) =>
		["expired", "missing"].includes(r.status),
	);

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Segreteria"
					subtitle="Quote, idoneità e adempimenti organizzativi"
					snapshot={s}
					scope={scope}
					area="segreteria"
					onReload={reload}
					reloading={loading}
					actions={
						<button
							className="ds-btn ds-btn--ghost"
							onClick={() => navigate("/secretariat")}
						>
							Apri segreteria
						</button>
					}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsKpiRow>
							<DsKpi
								label="Quote attese"
								value={formatEuro(fees.expected)}
								sub={`${fees.count} quote`}
							/>
							<DsKpi
								label="Incassato"
								value={
									fees.expected
										? Math.round((fees.paid / fees.expected) * 100) + "%"
										: "—"
								}
								sub={formatEuro(fees.paid)}
								tone="green"
							/>
							<DsKpi
								label="Rate scadute"
								value={formatEuro(fees.overdue)}
								tone={fees.overdue ? "red" : undefined}
								sub={`${fees.overduePlayers} posizioni quota da sollecitare`}
							/>
							<DsKpi
								label="Idoneità non valide"
								value={
									certificates?.tracked && !certificates.failed
										? certIssues.length
										: certificatesLoading && !certificates
											? "…"
											: "—"
								}
								tone={certIssues.length ? "red" : undefined}
								sub={
									certificates?.failed
										? "certificati non disponibili"
										: certificates && !certificates.tracked
											? "certificati non ancora inseriti"
											: "giocatori non impiegabili"
								}
							/>
						</DsKpiRow>

						{s.feesPreview && (
							<DsPreviewNotice>
								Il modulo quote non è ancora attivo sul server: importi e rate
								sono simulati sui giocatori reali delle rose.
							</DsPreviewNotice>
						)}
						<DsSection title="Quote per squadra" preview={s.feesPreview}>
							<DsTable
								columns={[
									{
										key: "name",
										label: "Squadra",
										render: (t) => <b>{t.name}</b>,
									},
									{
										key: "expected",
										label: "Attese",
										align: "right",
										render: (t) => formatEuro(t.fees.expected),
									},
									{
										key: "paid",
										label: "Incassato",
										render: (t) =>
											t.fees.expected ? (
												<div className="ds-inline">
													<DsBar
														value={t.fees.paid}
														max={t.fees.expected}
														tone={t.fees.overdue ? "yellow" : "green"}
													/>
													<span>
														{Math.round((t.fees.paid / t.fees.expected) * 100)}%
													</span>
												</div>
											) : (
												<span className="ds-muted">Nessuna quota</span>
											),
									},
									{
										key: "overdue",
										label: "Scaduto",
										align: "right",
										render: (t) =>
											t.fees.overdue ? (
												<DsStatus
													status="alert"
													label={formatEuro(t.fees.overdue)}
												/>
											) : (
												"—"
											),
									},
									{
										key: "families",
										label: "Famiglie",
										align: "center",
										render: (t) => t.fees.overduePlayers || "—",
									},
								]}
								rows={teams}
							/>
						</DsSection>

						<DsSection
							title="Idoneità sportiva"
							hint="Certificati medico-sportivi scaduti, assenti o in scadenza entro 30 giorni"
						>
							<DsTable
								columns={[
									{
										key: "player",
										label: "Giocatore",
										render: (r) => (
											<>
												<div className="ds-strong">{playerName(r.player)}</div>
												<div className="ds-muted">{dsMembershipLabel(r)}</div>
											</>
										),
									},
									{
										key: "status",
										label: "Stato",
										render: (r) => (
											<DsStatus
												status={CERT_STATUS[r.status]}
												label={CERT_LABEL[r.status]}
											/>
										),
									},
									{
										key: "expiry",
										label: "Scadenza",
										render: (r) =>
											r.certificate?.expiryDate
												? moment(r.certificate.expiryDate).format("D MMM YYYY")
												: "—",
									},
								]}
								rows={certRows}
								onRowClick={(r) => navigate("/ds/giocatore/" + r.id)}
								empty={
									!certificates
										? "Verifica dei certificati in corso…"
										: certificates.failed
											? "Certificati non disponibili o accesso non consentito."
											: certificates.tracked
												? "Tutti i certificati sono validi."
												: "Nessun certificato inserito: caricali dalla scheda di ogni giocatore per attivare il controllo idoneità."
								}
							/>
						</DsSection>

						<DsSection
							title="Altri adempimenti"
							preview
							action={<DsLink to="/secretariat">Segreteria</DsLink>}
						>
							<DsPreviewNotice />
							<div className="mt-3">
								<DsGrid min={220}>
									{PREVIEW_BLOCKS.map((b) => (
										<div
											key={b.title}
											className="ds-area ds-area--preview ds-area--static"
										>
											<div className="ds-area__head">
												<span className="ds-area__name">{b.title}</span>
												<DsStatus status={b.status} />
											</div>
											<div>
												<span className="ds-area__value">{b.value}</span>
												<span className="ds-area__unit">{b.unit}</span>
											</div>
											<ul className="ds-area__lines">
												{b.lines.map((l) => (
													<li key={l}>{l}</li>
												))}
											</ul>
										</div>
									))}
								</DsGrid>
							</div>
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsSecretariatPage;
